import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, inject, signal } from '@angular/core';
import { Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PaymentResult } from '@app/orders/models/orders-transaction.model';
import { TreeListRowData } from '@app/orders/models/orders.model';
import { OrdersProcessFacade } from '@app/orders/services/orders-process.facade';
import { PaymentTransactionService } from '@app/orders/services/payment-transaction.service';
import {
  RefundSummaryBuilderService,
  RefundSummaryItem,
} from '@app/orders/services/refund-summary-builder.service';
import { RightEnum } from '@core/models/rights.model';
import { PaymentModeResponseDto, PaymentTypeEnumRequestDto } from '@core/services/apiclient';
import { SalesService } from '@sales/services/sales.service';
import { AbstractPaymentModeFormComponent } from '@shared/components/functional/abstract-payment-mode-form/abstract-payment-mode-form.component';
import { CustomerDisplayItem } from '@shared/customer/models/customer-display-item.model';
import { CustomerService } from '@shared/customer/services/customer.service';
import { SalesPaymentModeModel } from '@shared/models/sales-payment-mode.model';
import { Observable, of, shareReplay, startWith, takeUntil } from 'rxjs';

@Component({
  selector: 'app-orders-payment-dialog',
  templateUrl: './orders-payment-dialog.component.html',
  styleUrl: './orders-payment-dialog.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrdersPaymentDialogComponent extends AbstractPaymentModeFormComponent {
  dialogRef = inject(MatDialogRef<OrdersPaymentDialogComponent>);
  selection = inject<TreeListRowData[]>(MAT_DIALOG_DATA);
  private readonly salesService = inject(SalesService);
  private readonly customerService = inject(CustomerService);
  private readonly refundSummaryBuilderService = inject(RefundSummaryBuilderService);
  private readonly paymentTransactionService = inject(PaymentTransactionService);
  private readonly ordersProcessFacade = inject(OrdersProcessFacade);
  private readonly cdr = inject(ChangeDetectorRef);
  readonly maxDisplayedLines = 5;
  isAllItemsDisplayed = false;
  selectedPaymentMode: PaymentModeResponseDto | null = null;
  selectedCustomer = signal<CustomerDisplayItem | null>(null);
  paymentsPayload: ReturnType<PaymentTransactionService['createPaymentPayload']>[] = [];
  availablePaymentModes: PaymentModeResponseDto[] = [];
  isPaymentBreakdown = false;
  paymentModes = signal<SalesPaymentModeModel[]>([]);
  paymentTypes = [PaymentTypeEnumRequestDto.Direct];
  amountToReturn: number;
  public RightEnum = RightEnum;

  private formValidSignal = signal<boolean>(false);

  readonly isConfirmDisabled = computed(() => {
    return (
      !this.formValidSignal() ||
      !this.selectedCustomer() ||
      !this.paymentModes().length ||
      this.isProcessing()
    );
  });

  private readonly isProcessingSignal = signal<boolean>(false);
  readonly isProcessing = computed(() => this.isProcessingSignal());

  get allSummaryItems(): RefundSummaryItem[] {
    return this.refundSummaryBuilderService.buildSummaryItems(this.selection ?? []);
  }

  get totalAmount(): number {
    return this.refundSummaryBuilderService.calculateTotalAmount(this.selection ?? []);
  }

  get summaryItems(): RefundSummaryItem[] {
    if (this.isAllItemsDisplayed || this.allSummaryItems.length <= this.maxDisplayedLines) {
      return this.allSummaryItems;
    }
    return this.allSummaryItems.slice(0, this.maxDisplayedLines);
  }

  get remainingLines(): number {
    return Math.max(0, this.allSummaryItems.length - this.maxDisplayedLines);
  }

  get hasMoreLines(): boolean {
    return this.allSummaryItems.length > this.maxDisplayedLines;
  }

  constructor() {
    super();
  }

  override ngOnInit(): void {
    this.paymentModes$ = this.getPaymentModes();
    this.totalAmount$ = this.getTotalAmount();
    this.initSelectedCustomer();
    this.initPaymentModes();
  }

  summaryLabel = (item: RefundSummaryItem) => item.label;

  onCustomerChange(customer: CustomerDisplayItem): void {
    this.selectedCustomer.set(customer);
    this.applyPaymentPayer();
  }

  paymentModeChange(mode: PaymentModeResponseDto | null): void {
    this.selectedPaymentMode = mode;
    if (this.isPaymentBreakdown) return;
    if (!mode) {
      this.paymentsPayload = [];
      this.paymentModes.set([]);
      return;
    }
    this.paymentsPayload = [this.paymentTransactionService.createPaymentPayload(mode, this.totalAmount)];
    if (this.paymentModes().length === 0) {
      this.paymentModes.set([this.paymentTransactionService.createPaymentMode(mode, this.totalAmount)]);
    }
    this.applyPaymentPayer();
  }

  onCancel(): void {
    this.dialogRef.close({ confirmed: false, response: null });
  }

  onConfirm(): void {
    if (this.form) {
      this.paymentsPayload = this.paymentTransactionService.buildPaymentPayloadFromForm(
        this.form,
        this.selectedCustomer()?.id ?? null,
        this.amountToReturn,
      );
      this.paymentModes.set(this.paymentTransactionService.updatePaymentModesFromForm(this.form));
    }

    if (!this.paymentsPayload.length || !this.selection?.length) {
      return;
    }

    this.isProcessingSignal.set(true);
    this.ordersProcessFacade
      .payOrderItemsAndSubscriptions(this.selection, this.paymentsPayload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result: PaymentResult) => {
          this.isProcessingSignal.set(false);
          this.dialogRef.close({
            confirmed: result.success,
            response: result.response,
            error: result.error,
          });
        },
      });
  }

  protected getPaymentModes(): Observable<PaymentModeResponseDto[]> {
    return this.salesService
      .getPaymentMode()
      .pipe(shareReplay({ bufferSize: 1, refCount: true }), takeUntil(this.destroy$));
  }

  protected getTotalAmount(): Observable<number> {
    return of(this.totalAmount);
  }

  public createForm(): void {
    const currentPaymentModes = this.paymentModes();
    if (!currentPaymentModes || currentPaymentModes.length === 0) {
      return;
    }
    const initialPaymentRows = currentPaymentModes.map((mode) => {
      return this.formBuilder.group({
        mode: [mode?.paymentMode, Validators.required],
        amount: [mode?.amount, [Validators.required, Validators.min(0)]],
      });
    });
    this.form = this.formBuilder.group(
      {
        rows: this.formBuilder.array(initialPaymentRows),
      },
      {
        validators: [this.hasUnfilledAmountValidator()],
        asyncValidators: [this.paymentModeAmountsSumValidator(this.totalAmount$)],
      },
    );
    this.setupFormStatusSubscription();
  }

  private setupFormStatusSubscription(): void {
    if (!this.form) {
      return;
    }
    this.form.statusChanges
      .pipe(startWith(this.form.status), takeUntil(this.destroy$))
      .subscribe((status) => {
        this.formValidSignal.set(status === 'VALID');
      });
  }

  private initSelectedCustomer(): void {
    if (!this.selection?.length) return;
    const customerId = this.selection?.[0]?.customer?.id;
    if (!customerId) return;
    this.customerService
      .getCustomerDisplayById(customerId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (customer) => {
          this.selectedCustomer.set(customer);
          this.applyPaymentPayer();
          this.cdr.markForCheck();
        },
      });
  }

  private applyPaymentPayer(): void {
    this.paymentsPayload = this.paymentTransactionService.applyPayer(
      this.paymentsPayload,
      this.selectedCustomer(),
    );
  }

  private initPaymentModes(): void {
    this.paymentModes$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (modes) => {
        this.availablePaymentModes = modes ?? [];
        if (modes?.length > 0) {
          const defaultMode = modes[0];
          this.selectedPaymentMode = defaultMode;
          this.paymentModeChange(defaultMode);
          this.createForm();
        }
        this.cdr.markForCheck();
      },
    });
  }
}
